"use client";

import React, { useState, useEffect } from "react";

const SmartFeatures = ({ content }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [isPaused, setIsPaused] = useState(false);
    const [itemsPerView, setItemsPerView] = useState(3); // Default: Large screen
    const totalItems = content?.services?.length || 0;
    const pauseDuration = 4000; // Pause before looping

    // Adjust items per view based on screen size
    useEffect(() => {
        const updateItemsPerView = () => {
            if (window.innerWidth >= 1024) {
                setItemsPerView(3); // Large screens
            } else if (window.innerWidth >= 660) {
                setItemsPerView(2); // Medium screens
            } else {
                setItemsPerView(1); // Small screens
            }
        };

        updateItemsPerView();
        window.addEventListener("resize", updateItemsPerView);

        return () => window.removeEventListener("resize", updateItemsPerView);
    }, []);

    // Prevent scrolling into empty space at the end
    const maxIndex = totalItems > itemsPerView ? totalItems - itemsPerView : 0;

    const nextSlide = () => {
        if (isTransitioning || isPaused) return;

        setIsTransitioning(true);

        if (currentIndex >= maxIndex) {
            setIsPaused(true);
            setTimeout(() => {
                setCurrentIndex(0);
                setTimeout(() => {
                    setIsPaused(false);
                    setIsTransitioning(false);
                }, 500);
            }, pauseDuration);
        } else {
            setCurrentIndex((prevIndex) => Math.min(prevIndex + 1, maxIndex));
            setTimeout(() => setIsTransitioning(false), 500);
        }
    };

    const prevSlide = () => {
        if (isTransitioning || isPaused) return;

        setIsTransitioning(true);
        setCurrentIndex((prevIndex) => Math.max(prevIndex - 1, 0));

        setTimeout(() => setIsTransitioning(false), 500);
    };

    // Auto-scroll every 3 seconds
    useEffect(() => {
        if (totalItems > 0) {
            const interval = setInterval(() => {
                if (!isTransitioning && !isPaused) {
                    nextSlide();
                }
            }, 3000);
            return () => clearInterval(interval);
        }
    }, [totalItems, isTransitioning, isPaused]);

    if (!content?.services || totalItems === 0) {
        return null;
    }

    return (
        <div className="md:container md:mt-24 mt-20 m-auto">
            <div className="flex flex-col justify-center items-center gap-4">
                {/* Title Section */}
                <div className="w-full flex flex-col justify-center items-center md:gap-4 gap-3">
                    <h3 className="text-black font-mono font-bold text-2xl md:text-4xl text-center">
                        {content?.title}
                    </h3>
                </div>

                {/* Main container with nav buttons and slider */}
                <div className="relative w-full flex items-center py-2 lg:mt-10 md:mt-7 mt-5">
                    {/* Left Navigation Button (Triangle) */}
                    {totalItems > itemsPerView && (
                        <button
                            name="btn"
                            aria-label="Previous Slide"
                            className={`flex-shrink-0 mr-4 h-12 w-12 flex justify-center items-center transition-all ease-in-out duration-300 ${currentIndex === 0
                                ? "opacity-50 cursor-not-allowed"
                                : "opacity-100 cursor-pointer hover:scale-125"
                                }`}
                            onClick={prevSlide}
                            disabled={
                                currentIndex === 0 ||
                                isTransitioning ||
                                isPaused
                            }
                        >
                            <div
                                className="w-0 h-0 border-y-8 border-y-transparent border-r-16 border-r-indigo-700"
                                style={{
                                    borderRightWidth: "16px",
                                    borderTopWidth: "12px",
                                    borderBottomWidth: "12px",
                                }}
                            />
                        </button>
                    )}

                    {/* Slider Container */}
                    <div className="flex-grow overflow-hidden">
                        <div
                            className="flex gap-8 transition-transform duration-500 ease-in-out w-full py-4"
                            style={{
                                transform: `translateX(-${(currentIndex * 100) / itemsPerView
                                    }%)`,
                            }}
                        >
                            {content.services.map((service, index) => (
                                <div
                                    key={`service-${index}`}
                                    className="p-6 flex flex-col justify-start items-start gap-2.5 bg-white shadow-md rounded-lg transition-all duration-500"
                                    style={{
                                        flex: `0 0 calc(${100 / itemsPerView
                                            }% - 29px)`,
                                    }}
                                >
                                    <span
                                        className={`w-9 h-9 flex justify-center items-center rounded-md text-white font-mono font-bold text-xl ${service?.bgColor}`}
                                    >
                                        {service?.id}
                                    </span>
                                    <h3 className="font-mono font-bold text-[17px] text-black mt-2.5">
                                        {service?.title}
                                    </h3>
                                    <p className="text-[#59587B] font-inter font-normal text-[15px]">
                                        {service?.description}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Right Navigation Button (Triangle) */}
                    {totalItems > itemsPerView && (
                        <button
                            aria-label="Slider"
                            name="slider"
                            className={`flex-shrink-0 ml-4 h-12 w-12 flex justify-center items-center transition-all ease-in-out duration-300 ${currentIndex === maxIndex
                                ? "opacity-50 cursor-not-allowed"
                                : "opacity-100 cursor-pointer hover:scale-125"
                                }`}
                            onClick={nextSlide}
                            disabled={
                                currentIndex === maxIndex ||
                                isTransitioning ||
                                isPaused
                            }
                        >
                            <div
                                className="w-0 h-0 border-y-8 border-y-transparent border-l-16 border-l-indigo-700"
                                style={{
                                    borderLeftWidth: "16px",
                                    borderTopWidth: "12px",
                                    borderBottomWidth: "12px",
                                }}
                            />
                        </button>
                    )}
                </div>

                {/* Indicator dots */}
                <div className="flex justify-center">
                    {Array.from({
                        length: totalItems - (itemsPerView - 1),
                    }).map((_, index) => (
                        <div 
                            name={`btn-${index + 1}`}
                            key={`dot-${index}`}
                            className={`w-2 h-2 mx-1 rounded-full mr-1 transition-all duration-500 ${index === currentIndex
                                ? "bg-blue-600 w-4"
                                : "bg-gray-300"
                                }`}
                            onClick={() => setCurrentIndex(index)}
                            disabled={isTransitioning || isPaused}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
};

export default SmartFeatures;
